import type { ReactNode } from 'react';
import { Layout } from '@components/Layout';

const LAST_UPDATED = 'March 27, 2026';

const sectionClassName = 'rounded-xl border border-zinc-800 bg-zinc-900/70 p-6 shadow-lg';
const listClassName = 'mt-3 list-disc space-y-2 pl-5 text-zinc-300';

interface SectionProps {
    title: string;
    children: ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
    <section className={sectionClassName}>
        <h2 className="text-2xl font-semibold text-white">{title}</h2>
        <div className="mt-4 space-y-4 text-zinc-300">{children}</div>
    </section>
);

const PrivacyPolicy = () => {
    return (
        <Layout title="Privacy Policy - Project 314">
            <div className="mx-auto my-6 max-w-4xl px-4">
                <div className={sectionClassName}>
                    <p className="text-sm font-medium uppercase tracking-[0.2em] text-zinc-400">Project 314</p>
                    <h1 className="mt-2 text-4xl font-semibold text-white">Privacy Policy</h1>
                    <p className="mt-3 text-sm text-zinc-400">Last updated: {LAST_UPDATED}</p>
                    <p className="mt-5 text-zinc-300">
                        This Privacy Policy describes how Meteoric, LLC (&quot;Meteoric,&quot; &quot;we,&quot; &quot;us,&quot; and
                        &quot;our&quot;) collects, uses, discloses, and protects information when you use Project 314 and related websites,
                        applications, integrations, and services (collectively, the &quot;Services&quot;). Project 314 is designed to help
                        users build, test, monitor, and deploy automated trading strategies that can connect to third-party brokerages and
                        market services.
                    </p>
                    <p className="mt-4 text-zinc-300">
                        Because the Services can involve sensitive financial and account information, we aim to explain our data practices
                        in plain language. Your use of the Services is also subject to any separate agreements, disclosures, or consents you
                        accept when connecting brokerage or other third-party accounts.
                    </p>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                    <div className={sectionClassName}>
                        <h2 className="text-xl font-semibold text-white">Quick Summary</h2>
                        <ul className={listClassName}>
                            <li>
                                We collect the information needed to create your account, connect brokerages, run strategies, and secure the
                                Services.
                            </li>
                            <li>
                                If you connect a brokerage, we may receive broker account data such as account identifiers, balances,
                                positions, orders, fills, and transaction history.
                            </li>
                            <li>
                                We use your information to operate Project 314, sync brokerage data, support automated trading workflows at
                                your direction, prevent abuse, and comply with legal obligations.
                            </li>
                            <li>
                                We may share information with brokers, infrastructure providers, and other service providers as needed to
                                operate the Services, but only as described in this policy.
                            </li>
                        </ul>
                    </div>

                    <div className={sectionClassName}>
                        <h2 className="text-xl font-semibold text-white">Important Notes</h2>
                        <ul className={listClassName}>
                            <li>
                                Brokerage, banking, market data, AI, and other third-party services have their own privacy practices. Review
                                their policies before connecting them to Project 314.
                            </li>
                            <li>
                                You can disconnect broker connections and update account settings inside the app, subject to technical and
                                legal retention requirements.
                            </li>
                            <li>
                                We use cookies and similar technologies for sign-in, session management, security, and service
                                functionality.
                            </li>
                            <li>The Services are intended for adults who are authorized to connect and manage their own accounts.</li>
                        </ul>
                    </div>
                </div>

                <div className="mt-6 space-y-6">
                    <Section title="1. Information We Collect">
                        <p>We may collect the following categories of information, depending on how you use the Services:</p>

                        <div>
                            <h3 className="text-lg font-medium text-white">Account and profile information</h3>
                            <ul className={listClassName}>
                                <li>Contact details such as your email address.</li>
                                <li>
                                    Authentication information such as hashed passwords, login activity, and two-factor authentication data.
                                </li>
                                <li>
                                    Account settings, preferences, and other information you choose to add to your profile or workspace.
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-white">Brokerage and financial account information</h3>
                            <ul className={listClassName}>
                                <li>
                                    Brokerage connection metadata such as broker name, connection status, scopes, and linked account
                                    identifiers.
                                </li>
                                <li>
                                    Broker-supplied information such as masked account numbers, balances, buying power, positions, orders,
                                    fills, executions, and transaction history.
                                </li>
                                <li>
                                    Connection credentials and tokens needed to maintain a brokerage integration, which may include
                                    broker-issued OAuth tokens, user-provided API keys, or credentials for user-managed OAuth apps,
                                    depending on the connection method you choose.
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-white">Strategy, portfolio, and workspace content</h3>
                            <ul className={listClassName}>
                                <li>
                                    Trading strategies, entry and exit logic, parameter settings, scorecards, readiness checklists,
                                    experiments, backtests, watchlists, notes, and portfolio configurations.
                                </li>
                                <li>
                                    Records associated with the operation of your strategies, such as signals, logs, trading account
                                    assignments, performance information, and strategy execution history.
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-white">Communications and AI feature inputs</h3>
                            <ul className={listClassName}>
                                <li>Messages you send to us or through in-product communication features.</li>
                                <li>
                                    Prompts, instructions, plan content, and related workspace context you submit when using AI, assistant,
                                    or autopilot-style features within the Services.
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium text-white">Technical and usage information</h3>
                            <ul className={listClassName}>
                                <li>IP address, browser type, operating system, device information, timestamps, and referring URLs.</li>
                                <li>
                                    Information about how you navigate and use the Services, including logs, diagnostics, and error reports.
                                </li>
                                <li>Cookie, session, and similar data used for authentication, security, and feature functionality.</li>
                            </ul>
                        </div>
                    </Section>

                    <Section title="2. How We Collect Information">
                        <p>
                            We collect information directly from you, automatically when you use the Services, and from third parties you
                            connect or instruct us to use.
                        </p>
                        <ul className={listClassName}>
                            <li>
                                You provide information when you register, log in, configure strategies, connect brokerages, or communicate
                                with us.
                            </li>
                            <li>
                                We collect technical and usage information automatically through cookies, logs, and similar service
                                functionality.
                            </li>
                            <li>
                                We receive information from brokerages and other third-party integrations when you authorize a connection or
                                direct the Services to retrieve data or submit instructions on your behalf.
                            </li>
                        </ul>
                    </Section>

                    <Section title="3. How We Use Information">
                        <p>We use personal information for legitimate business and operational purposes, including to:</p>
                        <ul className={listClassName}>
                            <li>Create and administer user accounts, authenticate users, and maintain account security.</li>
                            <li>Connect to brokerages and other third-party services, sync financial data, and maintain integrations.</li>
                            <li>
                                Build, test, monitor, and operate trading strategies, portfolios, and related automation features at your
                                direction, including generating and managing broker instructions associated with your configured workflows.
                            </li>
                            <li>Provide charts, backtests, scorecards, readiness tools, AI features, and other product functionality.</li>
                            <li>Respond to requests, provide support, communicate about the Services, and troubleshoot issues.</li>
                            <li>Detect, investigate, and prevent fraud, abuse, unauthorized activity, and security incidents.</li>
                            <li>
                                Improve, maintain, and develop the Services, including reliability, diagnostics, and feature performance.
                            </li>
                            <li>Comply with legal obligations, enforce agreements, and protect our rights, users, and the public.</li>
                        </ul>
                    </Section>

                    <Section title="4. How We Disclose Information">
                        <p>We may disclose information in the following circumstances:</p>
                        <ul className={listClassName}>
                            <li>
                                <strong className="text-white">With brokerages and integrations you choose to connect.</strong> We may send
                                or receive information needed to establish and maintain connections, retrieve account data, and carry out
                                actions you authorize through the Services.
                            </li>
                            <li>
                                <strong className="text-white">With service providers.</strong> We may use vendors that help us host,
                                secure, support, operate, and improve the Services, including providers for cloud infrastructure, storage,
                                authentication, communications, AI functionality, and technical operations.
                            </li>
                            <li>
                                <strong className="text-white">For legal and safety reasons.</strong> We may disclose information when
                                reasonably necessary to comply with law, regulation, legal process, or lawful governmental requests, or to
                                protect rights, safety, security, and property.
                            </li>
                            <li>
                                <strong className="text-white">In connection with a business transaction.</strong> Information may be
                                disclosed as part of a merger, acquisition, financing, reorganization, asset sale, or similar event.
                            </li>
                            <li>
                                <strong className="text-white">With your direction or consent.</strong> We may disclose information in other
                                situations if you ask us to or authorize us to do so.
                            </li>
                        </ul>
                    </Section>

                    <Section title="5. Brokerage Connections and Third-Party Services">
                        <p>
                            The Services are designed to work with third-party brokerages and related providers. When you connect a
                            brokerage or other third-party service, you authorize that provider to share information with us and, where
                            applicable, you authorize us to exchange information, requests, and instructions with that provider on your
                            behalf.
                        </p>
                        <p>
                            Third-party providers control their own privacy and security practices. Their handling of your information is
                            governed by their own terms and privacy policies, not this Privacy Policy. You should review those policies
                            before linking an external account or using a third-party feature.
                        </p>
                        <p>
                            If you choose a connection method that uses your own API keys, OAuth client credentials, or similar secrets, we
                            may store and use that information as needed to maintain the connection you requested.
                        </p>
                    </Section>

                    <Section title="6. Cookies, Tracking, and Do Not Track">
                        <p>
                            We use cookies and similar technologies for authentication, session management, security controls, fraud
                            prevention, load balancing, and remembering preferences and settings.
                        </p>
                        <p>
                            You may be able to adjust your browser settings to block or delete cookies, but parts of the Services may not
                            function properly if you do so.
                        </p>
                        <p>
                            Some browsers offer a &quot;Do Not Track&quot; setting. At this time, the Services do not respond to browser Do
                            Not Track signals because there is no universally accepted standard for doing so.
                        </p>
                    </Section>

                    <Section title="7. Data Retention">
                        <p>
                            We retain personal information for as long as reasonably necessary to provide the Services, maintain account
                            functionality, support lawful business operations, resolve disputes, enforce agreements, and comply with legal,
                            tax, accounting, regulatory, and recordkeeping obligations.
                        </p>
                        <p>
                            Retention periods may vary depending on the type of information, whether the data relates to financial or
                            transaction records, whether an account remains active, and whether deletion would interfere with legal,
                            security, or operational requirements.
                        </p>
                    </Section>

                    <Section title="8. Security">
                        <p>
                            We use reasonable administrative, technical, and organizational measures designed to protect personal
                            information. However, no method of transmitting or storing data is completely secure, and we cannot guarantee
                            absolute security.
                        </p>
                        <p>
                            You are responsible for maintaining the confidentiality of your credentials, using strong authentication
                            controls, and promptly notifying us if you believe your account or a connected integration has been compromised.
                        </p>
                    </Section>

                    <Section title="9. Your Choices and Privacy Rights">
                        <p>Depending on where you live and subject to applicable law, you may have rights to request that we:</p>
                        <ul className={listClassName}>
                            <li>Provide access to personal information we maintain about you.</li>
                            <li>Correct inaccurate personal information.</li>
                            <li>Delete certain personal information.</li>
                            <li>Provide information about how we collect, use, and disclose personal information.</li>
                        </ul>
                        <p>
                            We may need to verify your identity before acting on a request. We may also decline or limit a request where
                            permitted by law, including when data must be retained for security, legal, contractual, or operational reasons.
                        </p>
                        <p>
                            You can also review and update certain account information directly in the Services, disconnect linked
                            brokerages, rotate your credentials, and close your account subject to applicable retention requirements.
                        </p>
                    </Section>

                    <Section title="10. International Use">
                        <p>
                            Meteoric is based in the United States, and the Services are operated from the United States. If you use the
                            Services from outside the United States, you understand that your information may be transferred to, stored in,
                            and processed in the United States and other jurisdictions where our service providers operate.
                        </p>
                    </Section>

                    <Section title="11. Children">
                        <p>
                            The Services are not directed to children, and we do not knowingly collect personal information from anyone
                            under 18 years of age. If we learn that we have collected personal information from a child under 18 without
                            appropriate authorization, we will take steps to delete it as required by law.
                        </p>
                    </Section>

                    <Section title="12. Changes to This Privacy Policy">
                        <p>
                            We may update this Privacy Policy from time to time. When we do, we will revise the &quot;Last updated&quot;
                            date above and post the revised version through the Services. If changes are material, we may provide additional
                            notice as appropriate.
                        </p>
                    </Section>

                    <Section title="13. Contact Us">
                        <p>If you have questions about this Privacy Policy or want to submit a privacy request, contact us at:</p>
                        <address className="not-italic text-zinc-300">
                            Meteoric, LLC
                            <br />
                            701 Tillery St Ste 12 #B074
                            <br />
                            Austin, TX 78702
                            <br />
                            United States
                        </address>
                    </Section>
                </div>
            </div>
        </Layout>
    );
};

export default PrivacyPolicy;
